import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';

import { StorageProcessState } from '../hooks/useStorage';

import { getFileType } from './files';

const getProcessedAttachmentData = ({
  fileName,
  contentType,
  pathRef,
  fileSize,
}) => {
  const fileType = getFileType(contentType); // image, video, audio, etc...
  return {
    type: fileType,
    title: fileName,
    assetPath: pathRef,
    fileSize,
    mimeType: contentType,
  };
};

const AttachmentType = {
  AUDIO: 'audio',
  FILE: 'file',
  IMAGE: 'image',
  VIDEO: 'video',
};

const attachmentShape = PropTypes.shape({
  type: PropTypes.oneOf(Object.values(AttachmentType)),
  fileSize: PropTypes.number,
  assetPath: PropTypes.string,
  title: PropTypes.string,
  mimeType: PropTypes.string,
});

const attachmentPreviewPropTypes = {
  attachmentData: attachmentShape,
  assetLocalUrl: PropTypes.string.isRequired,
};

/**
 * Uploads attachments to firebase storage and returns the references
 *
 * @param {File[]} fileAttachments - Array of files to upload
 * @param {File} audioAttachment - Audio file to upload
 * @param {string} userId - User ID of who is uploading the attachments
 * @param {Function} uploadAttachments - Function to upload attachments
 * @param {Function} deleteAttachments - Function to delete attachments
 * @param {string} storagePath - Storage path to upload attachments to
 * @returns {Promise<{attachmentsRefs: string[], attachmentsData: Object[], storageTaskFailed: boolean}>}
 */
const uploadMessageAttachments = async ({
  fileAttachments,
  audioAttachment,
  userId,
  uploadAttachments,
  deleteAttachments,
  storagePath,
}) => {
  const attachmentsRefs = [];
  const attachmentsData = [];

  // If this function is called there is at least one file attachment or an audio attachment
  const allAttachments = audioAttachment ? [...fileAttachments, audioAttachment] : fileAttachments;

  const filesToAdd = allAttachments.map((file) => {
    const lastDotIndex = file.name.lastIndexOf('.');
    return {
      // This is then revoked once the upload finishes (correctly or not)
      dataUrl: URL.createObjectURL(file),
      format: file.name.slice(lastDotIndex + 1),
      fileName: file.name.slice(0, lastDotIndex),
      fileType: file.type,
    };
  });

  try {
    const result = await uploadAttachments(filesToAdd, storagePath);

    let storageTaskFailed = false;
    result.forEach((res) => {
      if (res.state === StorageProcessState.SUCCESS) {
        const {
          name,
          contentType,
          size,
        } = res.metadata;
        attachmentsRefs.push(res.fileRef);
        attachmentsData.push(getProcessedAttachmentData({
          fileName: name,
          pathRef: res.fileRef,
          contentType,
          fileSize: size,
        }));
      } else {
        storageTaskFailed = true;
      }
    });
    if (storageTaskFailed) {
      // If some upload fails, we should stop the sending process
      // and delete the files that were uploaded (if there is any)
      if (attachmentsRefs.length) {
        const filesToDelete = attachmentsRefs.map((fileRef) => ({ storagePath: fileRef }));
        await deleteAttachments(filesToDelete);
      }
    }
    return { attachmentsRefs, attachmentsData, storageTaskFailed };
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        userId,
      },
    });
    // Returning this to handle error messages from the caller function
    return { storageTaskFailed: true };
  }
};

export {
  getProcessedAttachmentData,
  AttachmentType,
  attachmentShape,
  attachmentPreviewPropTypes,
  uploadMessageAttachments,
};
